import React from 'react'
import type { ReactNode, ErrorInfo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLanguage } from '../i18n/index.jsx'
import { Sentry } from '../lib/sentry.js'

interface ErrorFallbackProps {
  fallbackMessage?: string
  onBack?: () => void
  onReset: () => void
  onHome?: () => void
}

function ErrorFallback({ fallbackMessage, onBack, onReset, onHome }: ErrorFallbackProps) {
  const { t } = useLanguage()
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>{t('somethingWentWrong')}</h2>
      <p style={{ color: 'var(--text-secondary)', margin: '1rem 0' }}>
        {fallbackMessage || t('unexpectedError')}
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        {onBack && (
          <button
            className="btn btn-secondary"
            onClick={onBack}
          >
            {t('goBack')}
          </button>
        )}
        <button
          className="btn btn-primary"
          onClick={onReset}
        >
          {t('tryAgain')}
        </button>
        {onHome && (
          <button
            className="btn btn-secondary"
            onClick={onHome}
          >
            {t('goToDashboard')}
          </button>
        )}
      </div>
    </div>
  )
}

interface ErrorBoundaryProps {
  children?: ReactNode
  fallbackMessage?: string
  onBack?: () => void
  onReset?: () => void
  onHome?: () => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo)
    Sentry.captureException(error, { extra: errorInfo })
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          fallbackMessage={this.props.fallbackMessage}
          onBack={this.props.onBack}
          onReset={() => {
            if (this.props.onReset) {
              this.props.onReset()
            } else {
              this.setState({ hasError: false, error: null })
            }
          }}
          onHome={this.props.onHome}
        />
      )
    }
    return this.props.children
  }
}

interface ModalErrorFallbackProps {
  onReset: () => void
}

function ModalErrorFallback({ onReset }: ModalErrorFallbackProps) {
  const { t } = useLanguage()
  return (
    <div style={{ padding: '1.5rem', textAlign: 'center' }}>
      <p style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>
        {t('somethingWentWrong')}
      </p>
      <button
        className="btn btn-secondary"
        style={{ marginTop: '0.75rem' }}
        onClick={onReset}
      >
        {t('tryAgain')}
      </button>
    </div>
  )
}

interface ModalErrorBoundaryProps {
  children?: ReactNode
}

interface ModalErrorBoundaryState {
  hasError: boolean
}

export class ModalErrorBoundary extends React.Component<ModalErrorBoundaryProps, ModalErrorBoundaryState> {
  constructor(props: ModalErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): ModalErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ModalErrorBoundary]', error, errorInfo)
    Sentry.captureException(error, { extra: errorInfo, tags: { surface: 'modal' } })
  }

  render() {
    if (this.state.hasError) {
      return (
        <ModalErrorFallback
          onReset={() => this.setState({ hasError: false })}
        />
      )
    }
    return this.props.children
  }
}

interface RouteErrorBoundaryProps {
  children?: ReactNode
}

export function RouteErrorBoundary({ children }: RouteErrorBoundaryProps) {
  const { t } = useLanguage()
  const location = useLocation()
  const navigate = useNavigate()

  // Key on location.key — React Router assigns a unique key to every navigation
  // entry, so back/forward always remounts and clears the error state.
  return (
    <ErrorBoundary
      key={location.key}
      fallbackMessage={t('pageEncounteredError')}
      onBack={() => navigate(-1)}
      onHome={() => navigate('/')}
    >
      {children}
    </ErrorBoundary>
  )
}
